import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Button } from '@domcrm/embed-ui';

import { listMyMinors } from '../widget-api/auth-minors';
import { createBooking, fetchAllowedAddons, listMyBookings, type AllowedAddonItem } from '../widget-api/bookings';
import { apiGet } from '../widget-api/http-client';
import { ApiError } from '../widget-api/http-client';
import { fetchScheduleSlots, type ScheduleSlotListItem } from '../widget-api/schedule-slots';
import { fetchSubscriptionsForSlot } from '../widget-api/subscriptions';
import { useEmbedBootstrap } from '../use-widget-bootstrap';
import { useAsync } from '../widget-hooks/useAsync';
import { usePDTransferConfirm } from '../widget-hooks/use-pd-transfer-confirm';
import { buildBookingsPayRedirect, bookingNeedsPayment, findActiveBookingForSlot, slotNeedsPayment } from '../widget-lib/booking-redirects';
import { MediaAvatar } from '../widget-ui/media-avatar';
import { PATH } from '../widget-lib/path-routes';
import { resolveSlotAccent } from '../widget-lib/slot-color';
import { resolvePaymentTTLMinutes } from '../widget-lib/payment-ttl';
import { AuthRequiredModal } from '../widget-ui/auth-required-modal';
import { BookingConfirmModal } from '../widget-ui/booking-confirm-modal';
import { BookingSuccessModal } from '../widget-ui/booking-success-modal';
import { EmbedMessageBlock } from '../widget-ui/embed-message-block';

function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function formatDay(iso: string): string {
  try {
    return new Date(`${iso}T12:00:00`).toLocaleDateString('ru-RU', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  } catch {
    return iso;
  }
}

/** Показываем баннер с ! и рамкой; текст делаем законченной фразой, если пришла одна строка без конца. */
function humanizeBookingErrorText(raw: string | null | undefined): string {
  if (raw == null || !String(raw).trim()) {
    return 'Не удалось оформить запись. Попробуйте ещё раз или обратитесь в студию.';
  }
  const t = String(raw).trim();
  if (t.includes('\n')) {
    return t;
  }
  const low = t.toLowerCase();
  if (low.includes('шлюз') && (low.includes('подключ') || low.includes('нет '))) {
    return 'Онлайн-оплата сейчас недоступна: в студии не настроены платёжные шлюзы. Попробуйте позже или обратитесь в студию.';
  }
  if (low.includes('в студи') && (low.includes('отключ') || low.includes('наличн'))) {
    return 'Оплата в студии в этой организации сейчас отключена. Оплатите онлайн или выберите другой вариант.';
  }
  if (!/[.!?…]$/.test(t) && !/[,;:)]$/.test(t)) {
    return `${t}.`;
  }
  return t;
}

type LocationState = { slot?: ScheduleSlotListItem };

type FormValues = {
  comment: string;
  represented_minor_id: string;
  payment_mode: 'online' | 'subscription' | 'cash';
  user_subscription_id: string;
};



export default function SessionPage() {
  const { slotId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const stateSlot = (location.state as LocationState | null)?.slot;
  const id = parseInt(String(slotId), 10);

  const {
    me,
    effectiveModules,
    orgSlug,
    branchId,
    widgetConfig,
    guestShowcaseOrg,
    organizationId,
    accessToken,
    bootstrapHint,
    loading: bootLoading,
    error: bootError,
    reloadBookings
  } = useEmbedBootstrap();
  const showQrFeature = (effectiveModules ?? me?.modules)?.qrcode === true;

  /** Слот с карточки расписания («все филиалы») несёт branch_id в state — используем как выбранный филиал. */
  const branchIdFromStateSlot = useMemo(() => {
    const raw = stateSlot?.branch_id;
    const branches = widgetConfig?.branches;
    if (raw == null || !branches?.length) {
      return null;
    }
    return branches.some((b) => b.id === raw) ? raw : null;
  }, [stateSlot?.branch_id, widgetConfig]);

  /**
   * Только для GET /schedule/slots: при сети из нескольких филиалов без branch в URL
   * не передаём branch_id — бэкенд отдаёт слоты всех филиалов (глубокая ссылка на /sessions/:id).
   */
  const slotFetchBranchId = useMemo(() => {
    if (!widgetConfig?.branches?.length) {
      return branchId;
    }
    if (widgetConfig.branches.length <= 1) {
      return widgetConfig.branches[0]?.id ?? null;
    }
    return branchId ?? branchIdFromStateSlot;
  }, [widgetConfig, branchId, branchIdFromStateSlot]);

  const qs = searchParams.toString();
  const suffix = qs ? `?${qs}` : '';

  const { data: resolvedSlot, loading: slotLoading, error: slotError } = useAsync(
    async () => {
      if (stateSlot && stateSlot.id === id) {
        return stateSlot;
      }
      if (!Number.isFinite(id) || (organizationId == null && !accessToken)) {
        return null;
      }
      const anchor = new Date();
      const from = addDays(anchor, -120);
      const to = addDays(anchor, 180);
      const slots = await fetchScheduleSlots({
        organizationId: accessToken ? null : organizationId,
        branchId: slotFetchBranchId,
        fromDate: toIsoDate(from),
        toDate: toIsoDate(to),
        accessToken
      });
      return slots.find((s) => s.id === id) ?? null;
    },
    [id, stateSlot, organizationId, accessToken, slotFetchBranchId]
  );

  const effectiveBranchId = useMemo(() => {
    if (!widgetConfig?.branches?.length) {
      return branchId;
    }
    if (widgetConfig.branches.length <= 1) {
      return widgetConfig.branches[0]?.id ?? null;
    }
    const slotBr = resolvedSlot?.branch_id;
    const slotBrOk =
      slotBr != null && widgetConfig.branches.some((b) => b.id === slotBr) ? slotBr : null;
    return branchId ?? branchIdFromStateSlot ?? slotBrOk;
  }, [widgetConfig, branchId, branchIdFromStateSlot, resolvedSlot?.branch_id]);

  const clientCommentEnabled = useMemo(() => {
    if (effectiveBranchId == null || !widgetConfig?.branches?.length) {
      return false;
    }
    const b = widgetConfig.branches.find((x) => x.id === effectiveBranchId);
    return b?.client_booking_comment_enabled === true;
  }, [widgetConfig, effectiveBranchId]);

  const isChild = (resolvedSlot?.service_group_type || '').toLowerCase() === 'child';

  const { data: minors } = useAsync(
    () => {
      if (!isChild || !accessToken) {
        return Promise.resolve([]);
      }
      return listMyMinors(accessToken);
    },
    [isChild, accessToken]
  );

  const { data: myBookings } = useAsync(
    async () => {
      if (!accessToken) {
        return [];
      }
      return listMyBookings(accessToken, organizationId);
    },
    [accessToken, organizationId]
  );

  const myBookingForThisSlot = useMemo(() => {
    if (!myBookings) return null;
    return myBookings.find((b) => b.slot_id === id && b.status !== 'cancelled');
  }, [myBookings, id]);

  const childrenBookedIds = useMemo(() => {
    if (!myBookings) return new Set<number>();
    return new Set(
      myBookings
        .filter((b) => b.slot_id === id && b.status !== 'cancelled' && b.represented_minor_id != null)
        .map((b) => b.represented_minor_id!)
    );
  }, [myBookings, id]);

  const availableMinors = useMemo(() => {
    if (!minors) return [];
    return minors.filter((m) => !childrenBookedIds.has(m.id));
  }, [minors, childrenBookedIds]);

  const { data: slotSubs, loading: subsLoading } = useAsync(
    async () => {
      if (!resolvedSlot?.id || !accessToken) {
        return [];
      }
      return fetchSubscriptionsForSlot(resolvedSlot.id, accessToken);
    },
    [resolvedSlot?.id, accessToken]
  );

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors, isSubmitting }
  } = useForm<FormValues>({
    defaultValues: {
      comment: '',
      represented_minor_id: '',
      payment_mode: 'online',
      user_subscription_id: ''
    }
  });

  const paymentMode = watch('payment_mode');
  const selectedSubId = watch('user_subscription_id');
  const [allowedAddons, setAllowedAddons] = useState<AllowedAddonItem[]>([]);
  const [selectedAddons, setSelectedAddons] = useState<number[]>([]);

  const selectedSubscription = useMemo(() => {
    if (!slotSubs || paymentMode !== 'subscription') return null;
    const sid = parseInt(selectedSubId, 10);
    if (!Number.isFinite(sid)) return null;
    return slotSubs.find((s) => s.id === sid) ?? null;
  }, [slotSubs, paymentMode, selectedSubId]);

  useEffect(() => {
    const svcId = resolvedSlot?.service_id;
    if (svcId == null || organizationId == null) {
      setAllowedAddons([]);
      setSelectedAddons([]);
      return;
    }
    let cancelled = false;
    const subId = paymentMode === 'subscription' ? selectedSubscription?.id ?? null : null;
    void (async () => {
      try {
        const res = await fetchAllowedAddons(
          svcId,
          organizationId,
          resolvedSlot?.branch_id ?? branchId ?? null,
          subId
        );
        if (!cancelled) {
          setAllowedAddons(res.items);
          const required = res.items.filter((x) => x.is_required).map((x) => x.id);
          setSelectedAddons((prev) => {
            const kept = prev.filter((id) => {
              const item = res.items.find((x) => x.id === id);
              if (!item) return false;
              if (subId != null && item.covered_by_plan !== true) return false;
              return true;
            });
            return Array.from(new Set([...kept, ...required]));
          });
        }
      } catch {
        if (!cancelled) {
          setAllowedAddons([]);
          setSelectedAddons([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    resolvedSlot?.service_id,
    resolvedSlot?.branch_id,
    organizationId,
    branchId,
    paymentMode,
    selectedSubscription?.id
  ]);

  const requiredAddonIds = useMemo(
    () => allowedAddons.filter((a) => a.is_required).map((a) => a.id),
    [allowedAddons]
  );
  const effectiveAddonIds = useMemo(
    () => Array.from(new Set([...selectedAddons, ...requiredAddonIds])),
    [requiredAddonIds, selectedAddons]
  );
  const addonsTotal = useMemo(
    () =>
      allowedAddons
        .filter((a) => effectiveAddonIds.includes(a.id))
        .reduce(
          (sum, a) =>
            sum + (paymentMode === 'subscription' && a.covered_by_plan === true ? 0 : Number(a.price) || 0),
          0
        ),
    [allowedAddons, effectiveAddonIds, paymentMode]
  );

  function toggleAddon(id: number): void {
    if (requiredAddonIds.includes(id)) return;
    setSelectedAddons((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  function isAddonSelectable(a: AllowedAddonItem): boolean {
    return paymentMode !== 'subscription' || a.covered_by_plan === true;
  }

  const serviceNeedsPayment = useMemo(() => slotNeedsPayment(resolvedSlot), [resolvedSlot]);
  const paymentTTLMinutes = useMemo(
    () => resolvePaymentTTLMinutes(widgetConfig, guestShowcaseOrg),
    [widgetConfig, guestShowcaseOrg]
  );
  const cashAtStudioEnabled = useMemo(
    () =>
      (widgetConfig?.client_booking_cash_at_studio_enabled === true ||
        guestShowcaseOrg?.client_booking_cash_at_studio_enabled === true) &&
      serviceNeedsPayment,
    [
      widgetConfig?.client_booking_cash_at_studio_enabled,
      guestShowcaseOrg?.client_booking_cash_at_studio_enabled,
      serviceNeedsPayment
    ]
  );

  useEffect(() => {
    if (!serviceNeedsPayment || slotSubs == null) return;
    if (slotSubs.length > 0) {
      const first = slotSubs[0];
      if (first) {
        setValue('payment_mode', 'subscription');
        setValue('user_subscription_id', String(first.id));
      }
    } else {
      setValue('payment_mode', 'online');
      setValue('user_subscription_id', '');
    }
  }, [slotSubs, serviceNeedsPayment, setValue]);

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successQRCode, setSuccessQRCode] = useState<{ pin: string; qr_data_url: string | null; lesson_name: string; starts_at: string; ends_at: string } | null>(null);
  const [qrcodeLoading, setQrcodeLoading] = useState(false);
  const [successNeedsPayment, setSuccessNeedsPayment] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  // Ф8 (ТЗ 5.14 п.4): уведомление А.3 и галочка на шаге онлайн-оплаты.
  const pdTransfer = usePDTransferConfirm({
    accessToken,
    organizationId,
    slotId: resolvedSlot?.id ?? null,
    enabled: confirmModalOpen && paymentMode === 'online'
  });
  const returnTo = `${location.pathname}${location.search}`;

  type QRItem = { pin: string; qr_data_url: string | null; lesson_name: string; starts_at: string; ends_at: string; created_at: string }; 
  const fetchQRCode = useCallback(async (token: string) => {
    setQrcodeLoading(true);
    try {
      const page = await apiGet<{ items: QRItem[]; total: number }>('/api/v1/qrcode/codes/active', { limit: '5' }, { accessToken: token });
      if (page.items && page.items.length > 0) {
        const newest = page.items.reduce((a, b) => new Date(a.created_at) > new Date(b.created_at) ? a : b);
        setSuccessQRCode(newest);
      } else {
        setSuccessQRCode(null);
      }
    } catch {
      setSuccessQRCode(null);
    } finally {
      setQrcodeLoading(false);
    }
  }, []);

  const onBook = handleSubmit((values) => {
    setSubmitError(null);
    if (!resolvedSlot || !accessToken) return;
    if (isChild) {
      const mid = parseInt(values.represented_minor_id, 10);
      if (!Number.isFinite(mid)) {
        setSubmitError('Выберите ребёнка для записи на детское занятие.');
        return;
      }
    }
    if (values.payment_mode === 'subscription') {
      const sid = parseInt(values.user_subscription_id, 10);
      if (!Number.isFinite(sid)) {
        setSubmitError('Выберите абонемент.');
        return;
      }
    }
    if (values.payment_mode === 'cash' && !cashAtStudioEnabled) {
      setSubmitError('Оплата в студии сейчас недоступна. Выберите другой способ или обновите страницу.');
      return;
    }
    setConfirmModalOpen(true);
  });

  const confirmBooking = useCallback(async () => {
    if (!resolvedSlot || !accessToken) return;
    setConfirmLoading(true);
    setSubmitError(null);
    setSuccessQRCode(null);
    setSuccessNeedsPayment(false);
    setSuccessModalOpen(false);
    setConfirmModalOpen(false);
    try {
      const vals = getValues();
      const returnUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;
      if (!serviceNeedsPayment) {
        await createBooking(
          {
            slot_id: resolvedSlot.id,
            comment: clientCommentEnabled ? vals.comment.trim() || null : null,
            represented_minor_id: isChild ? parseInt(vals.represented_minor_id, 10) : null,
            addons: effectiveAddonIds.length > 0 ? effectiveAddonIds : undefined
          },
          accessToken
        );
        setSuccessNeedsPayment(false);
        setSuccessModalOpen(true);
        fetchQRCode(accessToken);
        reloadBookings();
        return;
      }
      const useSubscription =
        vals.payment_mode === 'subscription' && slotSubs != null && slotSubs.length > 0;
      if (useSubscription) {
        const sid = parseInt(vals.user_subscription_id, 10);
        await createBooking(
          {
            slot_id: resolvedSlot.id,
            comment: clientCommentEnabled ? vals.comment.trim() || null : null,
            payment_method: 'subscription',
            user_subscription_id: sid,
            represented_minor_id: isChild ? parseInt(vals.represented_minor_id, 10) : null,
            addons: effectiveAddonIds.length > 0 ? effectiveAddonIds : undefined
          },
          accessToken
        );
        setSuccessNeedsPayment(false);
        setSuccessModalOpen(true);
        fetchQRCode(accessToken);
        reloadBookings();
        return;
      }
      if (vals.payment_mode === 'cash') {
        await createBooking(
          {
            slot_id: resolvedSlot.id,
            comment: clientCommentEnabled ? vals.comment.trim() || null : null,
            payment_method: 'cash',
            represented_minor_id: isChild ? parseInt(vals.represented_minor_id, 10) : null,
            addons: effectiveAddonIds.length > 0 ? effectiveAddonIds : undefined
          },
          accessToken
        );
        setSuccessNeedsPayment(true);
        setSuccessModalOpen(true);
        fetchQRCode(accessToken);
        reloadBookings();
        return;
      }
      const res = await createBooking(
        {
          slot_id: resolvedSlot.id,
          comment: clientCommentEnabled ? vals.comment.trim() || null : null,
          payment_method: 'online',
          return_url: returnUrl,
          represented_minor_id: isChild ? parseInt(vals.represented_minor_id, 10) : null,
          addons: effectiveAddonIds.length > 0 ? effectiveAddonIds : undefined,
          ...pdTransfer.payload
        },
        accessToken
      );
      if (res.payment_url) {
        window.location.assign(res.payment_url);
        return;
      }
      setSuccessNeedsPayment(false);
      setSuccessModalOpen(true);
      fetchQRCode(accessToken);
      reloadBookings();
    } catch (e) {
      // Сервер вернул свежий список получателей — показываем его и просим галочку снова.
      if (pdTransfer.applyServerError(e) && resolvedSlot) {
        setConfirmModalOpen(true);
      }
      if (e instanceof ApiError && e.errorType === 'BOOKING_DUPLICATE' && resolvedSlot) {
        const existingBooking = findActiveBookingForSlot(myBookings, resolvedSlot.id);
        if (existingBooking && bookingNeedsPayment(existingBooking)) {
          await navigate(buildBookingsPayRedirect(searchParams.toString(), existingBooking.id));
          return;
        }
      }
      if (e instanceof ApiError) {
        setSubmitError(e.detail);
      } else {
        setSubmitError(e instanceof Error ? e.message : 'Не удалось создать запись');
      }
    } finally {
      setConfirmLoading(false);
    }
  }, [
    resolvedSlot,
    accessToken,
    slotSubs,
    clientCommentEnabled,
    isChild,
    fetchQRCode,
    getValues,
    myBookings,
    navigate,
    searchParams,
    reloadBookings,
    effectiveAddonIds,
    serviceNeedsPayment,
    pdTransfer,
  ]);

  if (!Number.isFinite(id)) {
    return (
      <div className="w-full min-w-0">
        <p className="text-sm text-[var(--text-muted)]">Некорректный идентификатор слота.</p>
      </div>
    );
  }

  if (bootstrapHint && !orgSlug) {
    return (
      <div className="space-y-3 w-full min-w-0">
        <p className="text-sm text-[var(--text-muted)]">{bootstrapHint}</p>
      </div>
    );
  }

  if (bootLoading) {
    return (
      <div className="space-y-4 w-full min-w-0">
        <div className="h-7 w-3/4 animate-pulse rounded bg-[var(--bg-soft)]" />
        <div className="space-y-2">
          <div className="h-4 w-48 animate-pulse rounded bg-[var(--bg-soft)]" />
          <div className="h-4 w-32 animate-pulse rounded bg-[var(--bg-soft)]" />
          <div className="h-4 w-24 animate-pulse rounded bg-[var(--bg-soft)]" />
        </div>
      </div>
    );
  }

  if (bootError) {
    return (
      <div className="w-full min-w-0">
        <EmbedMessageBlock variant="error" size="field">
          {bootError}
        </EmbedMessageBlock>
      </div>
    );
  }

  if (slotLoading) {
    return (
      <div className="space-y-4 w-full min-w-0">
        <div className="border-b border-[var(--border)] pb-4">
          <div className="min-w-0">
            <div className="h-8 min-w-0 animate-pulse rounded bg-[var(--bg-soft)]" />
          </div>
        </div>
        <div className="space-y-2">
          <div className="h-4 w-56 animate-pulse rounded bg-[var(--bg-soft)]" />
          <div className="h-4 w-36 animate-pulse rounded bg-[var(--bg-soft)]" />
          <div className="h-4 w-24 animate-pulse rounded bg-[var(--bg-soft)]" />
        </div>
        <div className="h-9 w-28 animate-pulse rounded-xl bg-[var(--bg-soft)]" />
      </div>
    );
  }

  if (slotError) {
    return (
      <div className="space-y-2 w-full min-w-0">
        <EmbedMessageBlock variant="error" size="field">
          {slotError}
        </EmbedMessageBlock>
      </div>
    );
  }

  if (!resolvedSlot) {
    return (
      <div className="space-y-2 w-full min-w-0">
        <p className="text-sm text-[var(--text-muted)]">Слот не найден или недоступен.</p>
      </div>
    );
  }

  const isUserBooked = myBookingForThisSlot != null || (childrenBookedIds && childrenBookedIds.size > 0);

  if (!isUserBooked && (!resolvedSlot.is_available || resolvedSlot.places_left <= 0)) {
    return (
      <div className="flex flex-1 flex-col gap-4 px-4 pb-4 pt-3 sm:px-6">
        <header className="border-b border-[var(--border)] pb-4">
          <div className="min-w-0">
            <h1 className="text-xl font-bold leading-tight text-[var(--text)] sm:text-2xl">
              {resolvedSlot.service_name}
            </h1>
          </div>
        </header>
        <p className="text-sm text-[var(--text-muted)]">На это занятие нет свободных мест.</p>
      </div>
    );
  }

  const inputClass =
    'h-8 w-full rounded-sm border-0 bg-[var(--bg-soft)] px-3 py-1 text-[13px] text-[var(--text)] focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]';
  const textareaClass =
    'min-h-[5.5rem] w-full rounded-md border-0 bg-[var(--bg-soft)] px-3 py-2 text-[13px] text-[var(--text)] focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]';

  const deadlineMin = resolvedSlot.service_cancellation_deadline_minutes;
  const photoUrl = resolvedSlot.service_photo_url?.trim() || null;

  const priceLine =
    resolvedSlot.service_price != null && resolvedSlot.service_price > 0
      ? `${Math.round(resolvedSlot.service_price).toLocaleString('ru-RU')} ₽`
      : 'бесплатно';

  const serviceStripe = resolveSlotAccent(
    resolvedSlot.service_type_color,
    resolvedSlot.service_color
  );

  const isAlreadyBookedSelf = !isChild && myBookingForThisSlot != null;
  const isAlreadyBookedAllMinors =
    isChild && minors && minors.length > 0 && availableMinors.length === 0;

  return (
    <div className="flex flex-1 flex-col gap-4 px-4 pb-4 pt-3 sm:px-6">
      <header>
        <div className="min-w-0 pb-4">
          <h1 className="text-xl font-bold leading-tight text-[var(--text)] sm:text-2xl">
            {resolvedSlot.service_name}
          </h1>
        </div>
      </header>

      {photoUrl ? (
        <img
          src={photoUrl}
          alt=""
          className="h-40 w-full max-h-[14rem] rounded-lg border border-[var(--border)] object-cover sm:h-48"
        />
      ) : null}

      <section
        className="flex min-w-0 items-stretch overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--bg-elevated)]"
        aria-labelledby="session-details-heading"
      >
        <div
          className="w-1 shrink-0 self-stretch"
          style={{ backgroundColor: serviceStripe ?? 'var(--accent)' }}
          aria-hidden
        />
        <div className="min-w-0 flex-1 divide-y divide-[var(--border)]">
        <h2 id="session-details-heading" className="sr-only">
          Детали занятия
        </h2>
        <div className="px-4 py-3.5 sm:px-5 sm:py-4">
          <p className="text-[15px] leading-snug text-[var(--text)] sm:text-base">
            <span className="font-medium">{formatDay(resolvedSlot.slot_date)}</span>
            <span className="text-[var(--text-muted)]"> · </span>
            <span className="font-mono font-semibold tabular-nums">
              {resolvedSlot.is_all_day
                ? 'Весь день'
                : `${(resolvedSlot.start_time || '').slice(0, 5)}–${(resolvedSlot.end_time || '').slice(0, 5)}`}
            </span>
            {resolvedSlot.is_rental ? (
              <span className="ml-1.5 inline-flex rounded-full bg-[var(--bg-soft)] px-2 py-0.5 text-[10px] font-semibold text-[var(--text-muted)]">
                аренда
              </span>
            ) : null}
          </p>
        </div>
        {resolvedSlot.trainer_name || resolvedSlot.max_participants > 0 ? (
          <div className="grid gap-2 px-4 py-3.5 sm:grid-cols-2 sm:gap-6 sm:px-5 sm:py-3.5">
            {resolvedSlot.trainer_name ? (
              <div className="flex items-center gap-2">
                <MediaAvatar
                  photoUrl={resolvedSlot.trainer_photo_url}
                  label={resolvedSlot.trainer_name}
                  sizeClass="h-8 w-8"
                />
                <div>
                  <p className="text-xs text-[var(--text-muted)]">Тренер</p>
                  <p className="mt-0.5 text-sm font-medium text-[var(--text)]">{resolvedSlot.trainer_name}</p>
                </div>
              </div>
            ) : null}
            {resolvedSlot.max_participants > 0 ? (
              <div>
                <p className="text-xs text-[var(--text-muted)]">Свободно мест</p>
                <p className="mt-0.5 text-sm font-medium text-[var(--text)]">
                  {resolvedSlot.places_left} из {resolvedSlot.max_participants}
                </p>
              </div>
            ) : null}
          </div>
        ) : null}
        {deadlineMin != null && deadlineMin > 0 ? (
          <div className="px-4 py-3.5 sm:px-5">
            <p className="text-xs text-[var(--text-muted)]">Условия отмены</p>
            <p className="mt-0.5 text-sm text-[var(--text)]">
              {deadlineMin >= 60
                ? `можно отменить не позднее чем за ${Math.round(deadlineMin / 60)} ч до начала`
                : `можно отменить не позднее чем за ${deadlineMin} мин до начала`}
            </p>
          </div>
        ) : null}
        <div className="flex flex-wrap items-center justify-between gap-3 bg-[var(--bg-soft)] px-4 py-3.5 sm:px-5 sm:py-4">
          <span className="text-sm text-[var(--text-muted)]">Стоимость</span>
          <span className="text-xl font-bold tabular-nums text-[var(--text)] sm:text-2xl">{priceLine}</span>
        </div>
        </div>
      </section>

      {!accessToken ? (
        <div>
          <div className="space-y-3">
            <p className="text-sm text-[var(--text-muted)]">
              Чтобы записаться на это занятие, войдите или зарегистрируйтесь.
            </p>
            <Button
              type="button"
              className="w-full sm:w-auto"
              onClick={() => setAuthModalOpen(true)}
            >
              Записаться
            </Button>
            <AuthRequiredModal
              open={authModalOpen}
              onClose={() => setAuthModalOpen(false)}
              querySuffix={suffix}
              returnTo={returnTo}
              title="Вход для записи"
              description="Запись на занятие доступна после входа или регистрации. Выберите действие ниже."
            />
          </div>
        </div>
      ) : isAlreadyBookedSelf || isAlreadyBookedAllMinors ? (
        <div className="rounded-xl border border-[var(--border)] bg-[var(--bg-soft)] p-4 text-center space-y-3">
          <p className="text-sm font-semibold text-[var(--text)] flex items-center justify-center gap-1.5">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
            {isAlreadyBookedSelf
              ? 'Вы успешно записаны на это занятие!'
              : 'Все ваши подопечные уже записаны на это занятие.'}
          </p>
          {showQrFeature && (
            <p className="text-xs text-[var(--text-muted)]">
              Покажите QR-код на турникете студии или введите PIN из раздела «Мой доступ».
            </p>
          )}
          <div className="pt-1 flex flex-wrap items-center justify-center gap-2">
            {showQrFeature && (
              <Link
                to={`${PATH.myAccess}${suffix}`}
                className="inline-flex h-9 items-center justify-center rounded-xl bg-[var(--accent-bg)] px-4 text-xs font-medium text-[var(--accent-fg)] shadow-[var(--shadow-card)] transition-all hover:bg-[var(--accent-bg-hover)] active:scale-[0.97]"
              >
                📱 Показать QR-код прохода
              </Link>
            )}
            <Link
              to={`${PATH.bookings}${suffix}`}
              className="inline-flex h-9 items-center justify-center rounded-xl border border-[var(--border)] bg-[var(--bg-elevated)] px-4 text-xs font-medium text-[var(--text)] transition-all hover:bg-[var(--bg-hover)]"
            >
              Мои записи
            </Link>
          </div>
        </div>
      ) : (
        <form onSubmit={onBook} noValidate>
          <div className="space-y-4">
          {subsLoading ? (
            <p className="text-xs text-[var(--text-muted)]">Проверяем доступные абонементы…</p>
          ) : null}
          {!subsLoading && slotSubs && slotSubs.length === 0 ? (
            <div
              className="rounded-r-lg border border-[var(--border)] border-l-[3px] border-l-[var(--accent)] bg-[var(--bg-soft)] p-4 text-sm leading-relaxed text-[var(--text-muted)]"
              style={{ borderLeftColor: serviceStripe ?? undefined }}
              role="status"
            >
              <p>
                Подходящих абонементов для этой услуги нет. Продолжите запись ниже
                {resolvedSlot.service_price != null && resolvedSlot.service_price > 0
                  ? cashAtStudioEnabled
                    ? ' (онлайн или оплата в студии).'
                    : ' (при необходимости — оплата онлайн)'
                  : ''}
                {' '}
                или{' '}
                <Link
                  className="text-[var(--accent)] underline-offset-2 hover:underline"
                  to={`${PATH.subscriptionPlans}${suffix}`}
                >
                  выберите план в каталоге абонементов
                </Link>
                .
              </p>
            </div>
          ) : null}
          <input type="hidden" {...register('payment_mode')} />
          <input
            type="hidden"
            {...register('user_subscription_id', {
              validate: (v) =>
                paymentMode !== 'subscription' || (v && String(v).length > 0) || 'Выберите абонемент'
            })}
          />
          {paymentMode === 'cash' && cashAtStudioEnabled && serviceNeedsPayment ? (
            <p className="text-xs leading-relaxed text-[var(--text-muted)]">
              Оплату примут на ресепшене студии. Завершение оплаты отмечает администратор в CRM.
            </p>
          ) : null}
          {isChild ? (
            <div>
              <label className="mb-1 block text-[13px] text-[var(--text-muted)]" htmlFor="minor">
                Ребёнок
              </label>
              <select
                id="minor"
                className={`${inputClass} ${errors.represented_minor_id ? 'ring-1 ring-[var(--error-text)]' : ''}`}
                {...register('represented_minor_id', {
                  required: isChild ? 'Выберите ребёнка' : false
                })}
                defaultValue=""
              >
                <option value="" disabled>
                  Выберите…
                </option>
                {availableMinors.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
              {errors.represented_minor_id?.message ? (
                <div className="mt-1.5">
                  <EmbedMessageBlock variant="error" size="field" role="alert">
                    {String(errors.represented_minor_id.message)}
                  </EmbedMessageBlock>
                </div>
              ) : null}
              {isChild && minors && minors.length === 0 ? (
                <p className="mt-1 text-xs text-[var(--text-muted)]">
                  Дети не добавлены. Добавьте данные ребёнка в профиле CRM или в приложении организации.
                </p>
              ) : null}
            </div>
          ) : null}
          {clientCommentEnabled ? (
            <div>
              <label className="mb-1 block text-[13px] text-[var(--text-muted)]" htmlFor="comment">
                Комментарий (необязательно)
              </label>
              <textarea
                id="comment"
                rows={3}
                className={textareaClass}
                {...register('comment')}
              />
            </div>
          ) : null}
          {allowedAddons.length > 0 ? (
            <div>
              <p className="mb-1 block text-[13px] text-[var(--text-muted)]">
                Дополнительно к занятию (по желанию)
              </p>
              <div className="space-y-1.5 rounded-xl bg-[var(--bg-soft)] p-3">
                {allowedAddons.map((a) => {
                  const checked = selectedAddons.includes(a.id);
                  const covered = a.covered_by_plan === true;
                  const byPlan = paymentMode === 'subscription' && covered;
                  const selectable = isAddonSelectable(a);
                  return (
                    <label
                      key={a.id}
                      className={
                        'flex items-center gap-2.5 text-sm ' +
                        (selectable ? 'cursor-pointer' : 'cursor-not-allowed opacity-60')
                      }
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={!selectable || Boolean(a.is_required)}
                        onChange={() => selectable && toggleAddon(a.id)}
                        className="h-4 w-4 shrink-0"
                      />
                      <span className="min-w-0 flex-1 text-[var(--text)]">
                        {a.name}
                        {a.is_required ? (
                          <span className="ml-1.5 inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-900">
                            обязательно
                          </span>
                        ) : null}
                        {paymentMode === 'subscription' && covered ? (
                          <span className="ml-1.5 inline-flex rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold text-sky-800">
                            входит в абонемент
                          </span>
                        ) : null}
                        {paymentMode === 'subscription' && !covered ? (
                          <span className="ml-1.5 inline-flex rounded-full bg-[var(--bg-elevated)] px-2 py-0.5 text-[10px] font-semibold text-[var(--text-muted)]">
                            только отдельно
                          </span>
                        ) : null}
                      </span>
                      <span className="shrink-0 text-[13px] font-semibold tabular-nums text-[var(--text)]">
                        {byPlan
                          ? '0 ₽ по абонементу'
                          : Number(a.price) > 0
                            ? `+${Math.round(Number(a.price)).toLocaleString('ru-RU')} ₽`
                            : 'Бесплатно'}
                      </span>
                    </label>
                  );
                })}
                {addonsTotal > 0 ? (
                  <p className="pt-1 text-right text-[13px] font-semibold tabular-nums text-[var(--text)]">
                    Итого допы: {Math.round(addonsTotal).toLocaleString('ru-RU')} ₽
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}
          {submitError ? (
            <EmbedMessageBlock variant="error" size="banner" aria-live="assertive" role="alert">
              {humanizeBookingErrorText(submitError)}
            </EmbedMessageBlock>
          ) : null}
          <BookingConfirmModal
            open={confirmModalOpen}
            onClose={() => { if (!confirmLoading) setConfirmModalOpen(false); }}
            onConfirm={confirmBooking}
            loading={confirmLoading}
            slot={resolvedSlot}
            paymentMode={paymentMode}
            selectedSubscription={selectedSubscription}
            slotSubs={slotSubs ?? []}
            cashAtStudioEnabled={cashAtStudioEnabled}
            serviceNeedsPayment={serviceNeedsPayment}
            paymentTTLMinutes={paymentTTLMinutes}
            addonsTotal={addonsTotal}
            addonsCount={effectiveAddonIds.length}
            onSelectPaymentMode={(mode) => setValue('payment_mode', mode)}
            onSelectSubscription={(subId) => setValue('user_subscription_id', subId)}
            pdTransferNotice={pdTransfer.notice}
            pdTransferLoading={pdTransfer.loading}
            pdTransferError={pdTransfer.error}
            pdTransferConfirmed={pdTransfer.confirmed}
            onPdTransferConfirmedChange={pdTransfer.setConfirmed}
          />
          <BookingSuccessModal
            open={successModalOpen}
            onClose={() => setSuccessModalOpen(false)}
            qrCode={successQRCode}
            loading={qrcodeLoading}
            isPaymentRequired={successNeedsPayment}
          />
          <Button
            type="submit"
            className="w-full sm:w-auto"
            disabled={isSubmitting || subsLoading}
          >
            {isSubmitting ? 'Отправляем…' : subsLoading ? 'Подождите…' : 'Записаться'}
          </Button>
          </div>
        </form>
      )}
    </div>
  );
}
